import * as ents from '@attain-apps/entities'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as dimensionFilter from '../plugin'

import * as effects from './effects'

/** Dimension filter synchronized with a collection's Current List filters. */
export abstract class Plugin<
  TTypeParams extends Plugin.TypeParams = Plugin.TypeParams,
  TId extends ec.plugins.Plugin.Id.Custom = ec.plugins.Plugin.Id.Custom,
> extends dimensionFilter.Plugin<TTypeParams, TId> {
  /** Accesses this plugin's dimension filter within Current List filters. */
  public readonly currentListFilter: Plugin.CurrentListFilter<TTypeParams>

  /** Applied selections retained so Current List edits can be discarded. */
  private readonly _appliedSelectionStorage =
    new store.storage.included.SessionStorage({
      parent: this.feature,
      properties: {
        lookup: store.storage.lookup<
          ents.core.dms.models.Entity.Id,
          readonly ents.core.dms.data.Record.Id[]
        >({
          get: (key, ids = []) => ids,
        }),
      },
      name: 'AppliedSelection',
    })

  protected constructor({
    collection,
    currentListFilterKey,
    dimensionId,
    id,
  }: Plugin.Config<TTypeParams, TId>) {
    super({ collection, dimensionId, id })

    this.currentListFilter = {
      get: filters =>
        filters?.[currentListFilterKey] &&
        TypeAsserter.validate(
          filters[currentListFilterKey],
          ents.extra.space.Record.Filter.DimensionFilter
            .assertIsDimensionFilter,
        ),
      set: (filters, value) => ({
        ...filters,
        [currentListFilterKey]: value,
      }),
    }

    this.feature.registerEffects(
      effects.applySelectionsOnParamsApplied(
        this,
        this._appliedSelectionStorage,
      ),
      effects.clearAppliedSelectionsOnReset(this._appliedSelectionStorage),
      effects.clearSelectionOnFilterCleared(this, this.selectionStorage),
      effects.discardSelectionsOnParamsDiscarded(
        this,
        this._appliedSelectionStorage,
        this.selectionStorage,
      ),
      effects.initializeSelection(
        this._appliedSelectionStorage,
        this.selectionStorage,
      ),
      effects.setEditedFilterOnSelection(this),
    )
  }
}

export namespace Plugin {
  /** Immutable access to one DimensionFilter property in Current List filters. */
  export interface CurrentListFilter<
    TTypeParams extends TypeParams = TypeParams,
  > {
    /** Reads this plugin's DimensionFilter from Current List filters. */
    get(
      filters: TTypeParams['filters'] | undefined,
    ): ents.extra.space.Record.Filter.DimensionFilter | undefined

    /** Returns Current List filters with this plugin's DimensionFilter set. */
    set(
      filters: TTypeParams['filters'] | undefined,
      value: ents.extra.space.Record.Filter.DimensionFilter | undefined,
    ): TTypeParams['filters']
  }

  export namespace CurrentListFilter {
    /** Keys whose non-null value is a DimensionFilter. */
    export type Key<TTypeParams extends TypeParams> = {
      [TKey in keyof TTypeParams['filters']]-?: NonNullable<
        TTypeParams['filters'][TKey]
      > extends ents.extra.space.Record.Filter.DimensionFilter
        ? TKey
        : never
    }[keyof TTypeParams['filters']]
  }

  export type TypeParams = ec.models.TypeParams.Constraint<{
    context: ents.core.admin.Context.ForScenario | undefined
    filters: object
  }>

  export interface Config<
    TTypeParams extends Plugin.TypeParams,
    TId extends ec.plugins.Plugin.Id.Custom,
  > extends dimensionFilter.Plugin.Config<TTypeParams, TId> {
    readonly currentListFilterKey: CurrentListFilter.Key<TTypeParams>
  }
}
